import { useBunja } from 'bunja/react';
import { useAtomValue } from 'jotai';
import { useTranslations } from 'next-intl';
import { memo } from 'react';

import { Pnl } from '@flipster/features/src/shared/pnl';
import { ProfitBoostBadge } from '@flipster/features/src/shared/position/profit-boost-badge';
import Formatted from '@flipster/react/design-utils/formatted';
import ChevronRightIcon from '@flipster/react/icon/generated/ChevronRightIcon';
import Share2Icon from '@flipster/react/icon/generated/Share2Icon';
import { css } from '@flipster/styled-system/css';
import { flex, hstack } from '@flipster/styled-system/patterns';
import { token } from '@flipster/styled-system/tokens';
import { TableCell } from '@flipster/ui/Table';
import { UsdtPrecision } from '@flipster/utils/constants/currency';
import Num from '@flipster/utils/num';

import { openPositionRowDataBunja } from '../row-bunja';
import { useOpenPositionRowActions } from '../use-open-position-row-actions';

/**
 * PnL 값 표시 (기존 PnlModeDisplay 대체 — 표시값 계산은 데이터 레이어의
 * displayPnlAtom/displayPnlPercentAtom이 담당). string 구독이라 값이 바뀐 tick에만 리렌더된다.
 */
const PnlValue = () => {
  const { displayPnlAtom, displayPnlPercentAtom } = useBunja(openPositionRowDataBunja);
  const displayPnl = useAtomValue(displayPnlAtom);
  const displayPnlPercent = useAtomValue(displayPnlPercentAtom);
  const { openPnlBreakdownModal } = useOpenPositionRowActions();

  return (
    <button type='button' onClick={openPnlBreakdownModal} className={hstack({ gap: 4 })}>
      <Pnl
        number={displayPnl}
        percent={displayPnlPercent}
        css={{ textStyle: 'Text/S/Monospace/Strong', whiteSpace: 'nowrap' }}
      >
        <Pnl.Number /> <Pnl.Percent />
      </Pnl>
      <ChevronRightIcon size={16} color={token('colors.content.default.level-3')} />
    </button>
  );
};

/** boost 뱃지 — 노출 여부(boolean) + positionId(안정 primitive)만 구독. ProfitBoostBadge가 id로 self-subscribe한다 */
const PnlBoostBadge = () => {
  const { showProfitBoostBadgeAtom, positionIdAtom, symbol } = useBunja(openPositionRowDataBunja);
  const showProfitBoostBadge = useAtomValue(showProfitBoostBadgeAtom);
  const positionId = useAtomValue(positionIdAtom);

  if (!showProfitBoostBadge || !positionId) return null;
  return <ProfitBoostBadge positionId={positionId} symbol={symbol} location='order_form_page' />;
};

const PnlAirdropBadge = () => {
  const transAssets = useTranslations('Assets.r250401');
  const { isAirdropAtom } = useBunja(openPositionRowDataBunja);
  const isAirdrop = useAtomValue(isAirdropAtom);

  if (!isAirdrop) return null;
  return (
    <span
      className={hstack({
        h: 20,
        px: 8,
        rounded: 99,
        color: 'inverse.white',
        bgGradient: 'programs.promotion',
        textStyle: 'Text/XS/Regular',
      })}
    >
      {transAssets('Airdrop')}
    </span>
  );
};

/** Boosted profit 박스 — 부스팅 여부·누적 pnl만 구독 */
const BoostedProfitBox = () => {
  const transAssets = useTranslations('Assets.r250401');
  const { isBoostingAtom, boostCumPnlAtom } = useBunja(openPositionRowDataBunja);
  const isBoosting = useAtomValue(isBoostingAtom);
  const boostCumPnl = Num(useAtomValue(boostCumPnlAtom) || 0);

  if (!isBoosting && !boostCumPnl.isGreaterThan(0)) return null;
  return (
    <div
      className={hstack({
        position: 'relative',
        p: 1,
        w: 200,
        textStyle: 'Text/XS/Regular',
        rounded: 8,
        overflow: 'hidden',
      })}
    >
      <div
        className={hstack({
          justifyContent: 'space-between',
          bgColor: 'background.default.level-1',
          width: '100%',
          px: 8,
          py: 4,
          zIndex: 1,
          rounded: 8,
        })}
      >
        <span>{transAssets('Boosted_profit')}</span>
        <Formatted.Number
          value={boostCumPnl.toString()}
          precision={UsdtPrecision}
          toFixed
          withColor
          withPlus
          css={{ textStyle: 'Text/S/Monospace/Strong' }}
        />
      </div>
      {isBoosting && (
        <div
          style={{
            width: 200,
            height: 200,
          }}
          className={css({
            position: 'absolute',
            display: 'block',
            content: '""',
            background:
              'conic-gradient(#3326C8 0%, #3326C8 25%, #B08AFF 40%, #7815DB 83.3%, #B08AFF 90%, #3326C8 100%)',
            animation: 'absoluteRotate 4s linear infinite',
            backgroundSize: 'cover',
            top: '50%',
            left: '50%',
            transformOrigin: '0% 0%',
          })}
        />
      )}
    </div>
  );
};

/** 레이아웃 셸 — 데이터 구독 없음. 각 섹션이 자기 atom만 구독해 딱 변하는 부분만 리렌더된다 */
const PnlCell = memo(() => {
  const { pnlShareClick } = useOpenPositionRowActions();

  return (
    <TableCell>
      <div className={hstack({ gap: 8, flexWrap: 'nowrap' })}>
        <div>
          <div className={hstack({ gap: 8, alignItems: 'center', mb: 4 })}>
            <div className={flex({ align: 'center', gap: 4 })}>
              <PnlValue />
              <PnlBoostBadge />
              <PnlAirdropBadge />
            </div>
          </div>
          <BoostedProfitBox />
        </div>
        <button onClick={pnlShareClick} className={css({ color: 'content.default.level-2' })}>
          <Share2Icon size={16} />
        </button>
      </div>
    </TableCell>
  );
});

export default PnlCell;
